import { Map } from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';

import QPureComponent from 'QPureComponent';
// Custom
import StdDialog from 'components/Dialogs/StdDialog';
import SearchBox from 'components/SearchBox';
import TransactionSelectList from 'components/TransactionSelectList';
import { getLesserBilledTransactions } from 'data/transactionList/selectors';

const styles = (theme) => ({
  billableContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  billable: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: 8,
  },
  dialogRoot: {
    overflow: 'auto',
  },
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    height: 'fit-content',
    backgroundColor: theme.palette.background.paper,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    flexShrink: 2,
  },
  searchBar: {
    height: 40,
    paddingLeft: 4,
    borderRadius: 12,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    margin: 'auto 0 0 4px',
    '&.open': {
      width: 220,
    },
    '&:hover': {
      bordercolor: theme.palette.text.primary,
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    fontSize: '14px',
    lineHeight: 1.43,
    fontWeight: 'normal',
    color: theme.palette.searchInput,
    '&:-webkit-autofill': {
      // disable autofill color because it makes it ugly
      // '-webkit-background-clip': 'text',
    },
  },
  greyGrid: {
    padding: '0px 14px',
    position: 'relative',
  },
  buttonBottoms: {
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 68,
    paddingBottom: 14,
    paddingLeft: 24,
    paddingRight: 24,
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.border.light}`,
  },
  manualLine: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    color: '#666666',
  },
  manualButton: {
    color: theme.palette.secondary.main,
    marginLeft: 6,
  },
  disabledSave: {
    color: `${theme.palette.common.white} !important`,
    opacity: 0.5,
  },
  processing: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.applyOpacityToHex(theme.palette.background.paper, 0.5),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

class SelectTransactionDialog extends QPureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selected: null, // if multiSelect, will be an ImmutableMap of selected txn
      processing: false, // used for spinner in Bill Presentment
    };
    this.autoTimeout = null;
  }

  componentWillUnmount() {
    if (this.autoTimeout) {
      clearTimeout(this.autoTimeout);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) {
      // clear state if component is closed by parent but not unmounted
      this.resetState();
      if (this.autoTimeout) {
        clearTimeout(this.autoTimeout);
      }
    }
  }

  resetState = () => {
    this.setState({
      selected: null,
      search: null,
      processing: false,
    });
  };

  // This is the function by which a transaction selected from the virtualList in ScheduleList component is stored in
  // this component's state. Couldn't think of a cleaner way to back-pass updates to state. The nested ID checks are
  // used to grab parent ID values, as each transaction row has many child components, but the ID we need is in the
  // parent row.
  selectTxn = (event) => {
    const newSelect = JSON.parse(event.currentTarget.id);
    let currentSelect = this.state.selected;
    if (this.props.multiSelect) {
      currentSelect = currentSelect || Map();
      if (currentSelect?.size > 0 && currentSelect.has(newSelect.id)) {
        this.setState({ selected: currentSelect.delete(newSelect.id) });
      } else {
        this.setState({ selected: currentSelect.set(newSelect.id, newSelect) });
      }
      return;
    }
    if (currentSelect && currentSelect.id === newSelect.id) {
      this.setState({ selected: null });
    } else {
      this.setState({ selected: newSelect });
    }
  };

  handleComplete = () => {
    const { onTransactionSelected } = this.props;
    if (onTransactionSelected) onTransactionSelected(this.state.selected);
    this.setState({ processing: true });
    this.autoTimeout = setTimeout(this.resetState, 5000);
  };

  onClose = () => {
    this.props.onClose?.();
    this.resetState();
  };

  render() {
    const {
      classes,
      selectorProps,
      title,
      buttonText,
      disableAdd,
      subtitle,
      onTransactionSelected,
      open,
      transactions,
      transactionListSelector,
      filterByType,
      multiSelect,
      checkedText,
      onChangeChecked,
      checked,
      ...otherProps
    } = this.props;

    const { selected, processing } = this.state;

    // For backwards compatibility, the default selector is for scheduled transactions
    const derivedSelector = transactionListSelector || getLesserBilledTransactions;
    return (
      <StdDialog
        className={classes.dialogRoot}
        onClose={this.onClose}
        open={Boolean(open)}
        sharedcomponentid={'SELECT_TRANSACTION_DIALOG'}
        size='md'
        title={title || 'Select Transaction'}
        {...otherProps}
      >
        <div className={classes.headerSection}>
          <Typography className={classes.subtitle}>{subtitle || 'Select or search for a transaction'}</Typography>

          <SearchBox
            onSearch={(search) => {
              this.setState({ search });
            }}
            autoSearch
            autoFocus
            collapsable={false}
            initialValue={this.state.search}
            placeholder='Search Transactions'
            classes={{
              root: classes.searchBar,
              input: classes.searchInput,
            }}
          />
        </div>
        <DialogContent className={classes.greyGrid}>
          <TransactionSelectList
            clickFunc={this.selectTxn}
            highlight={selected?.map?.((t) => t.id) || selected?.id}
            filter={this.state.search}
            onReset={() => this.setState({ search: null })}
            transactions={transactions}
            transactionListSelector={derivedSelector}
            selectorProps={selectorProps}
            filterByType={filterByType}
            multiSelect={multiSelect}
          />

          {processing && (
            <div className={classes.processing}>
              <CircularProgress size={64} className={classes.spinner} />
            </div>
          )}
        </DialogContent>
        <div className={classes.buttonBottoms}>
          <div className={classes.billableContainer}>
            {checkedText && (
              <div className={classes.billable}>
                <Checkbox checked={checked} onChange={onChangeChecked} />
                <Typography variant='bodyRegular'>{checkedText}</Typography>
              </div>
            )}
            <Button
              type='submit'
              id='create-transaction'
              variant='contained'
              disabled={processing || !selected || (multiSelect && selected?.isEmpty())}
              onClick={this.handleComplete}
            >
              {buttonText || 'CREATE'}
            </Button>
          </div>

          {!disableAdd && (
            <Typography variant='paragraphRegular' className={classes.manualLine}>
              {"Couldn't find?"}
              <Button
                className={classes.manualButton}
                id='add-manually-transaction'
                onClick={() => onTransactionSelected && onTransactionSelected()}
              >
                ADD MANUALLY
              </Button>
            </Typography>
          )}
        </div>
      </StdDialog>
    );
  }
}

SelectTransactionDialog.propTypes = {
  classes: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onTransactionSelected: PropTypes.func,
  transactions: PropTypes.object,
  transactionListSelector: PropTypes.func,
  selectorProps: PropTypes.object,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttonText: PropTypes.string,
  disableAdd: PropTypes.bool,
  filterByType: PropTypes.func,
  multiSelect: PropTypes.bool,
  checked: PropTypes.bool,
  checkedText: PropTypes.string,
};

export default withStyles(SelectTransactionDialog, styles);
